import { z } from 'zod';

/** Search params for the Tax Lots route. Mirrors the tax-transactions pattern:
 * filter / sort / page state lives in the URL so it survives reload, back/forward
 * and is shareable. Tolerant coercions (numberish/stringArrayish) keep hand-edited
 * or legacy URLs from throwing — anything invalid falls back to the default. */
const numberish = z
  .union([z.number(), z.string()])
  .transform((v) => {
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? n : undefined;
  })
  .optional()
  .catch(undefined);

const stringArrayish = z
  .union([z.array(z.union([z.string(), z.number()])), z.string(), z.number()])
  .transform((v) => {
    if (Array.isArray(v)) return v.map((x) => String(x));
    // support comma-separated query strings as well as single values
    const s = String(v);
    return s.includes(',') ? s.split(',').filter(Boolean) : [s];
  })
  .optional()
  .catch(undefined);

export const taxLotsSearchSchema = z
  .object({
    // open vs closed feed
    status: z.enum(['OPEN', 'CLOSED']).optional().catch(undefined),

    // page details
    pageSize: numberish,
    index: numberish,
    cursor: z.string().optional().catch(undefined),
    direction: z.string().optional().catch(undefined),

    // sorting (sortColumn must be a backend SortColumnForTaxLots value)
    sortColumn: z
      .enum([
        'acquired',
        'disposed',
        'quantity',
        'costBasis',
        'proceeds',
        'realizedGainLoss',
        'holdingTermDays',
        'currency',
      ])
      .optional()
      .catch(undefined),
    sortOrder: z.enum(['asc', 'desc']).optional().catch(undefined),

    // drawer filters
    holding: z.enum(['ALL', 'SHORT', 'LONG']).optional().catch(undefined),
    assets: stringArrayish,
    sources: stringArrayish,
    acquiredFrom: z.string().optional().catch(undefined),
    acquiredTo: z.string().optional().catch(undefined),
    minBasis: numberish,
    maxBasis: numberish,
  })
  .catch({});
